import { Fragment, type ReactNode } from 'react';
import { sortBy } from 'lodash';
import { useMedia } from 'react-use';
import {
  getWarningText,
  type DietaryRestrictionsText,
} from '@hungryroot/dietary-restrictions';
import {
  WarningText,
  WarningTextStyleVariant,
  IngredientsImageGrid,
} from '@hungryroot/pho';
import { type PairingV1 as Pairing } from '@hungryroot/rest';
import { tagGroups, useTagGroup } from '@hungryroot/tags';
import { BreakpointQueryLarge } from '@hungryroot/tokens';
import Header from '../../../frontend/react/src/generic/pairing_cards_v2/components/Header';
import Instructions from '../../../frontend/react/src/generic/pairing_cards_v2/components/Instructions';
import NutritionInfo from '../../../frontend/react/src/generic/pairing_cards_v2/components/NutritionInfo';
import {
  copySharableLink,
  getPairingDataImg,
} from '../../../frontend/react/src/utils/utils';
import { Ingredients } from '../../../pages/customize/components/Ingredients/Ingredients';
import { AuthGuard } from '../../auth/AuthGuard/AuthGuard';
import { type PairingWithIngredientDetails } from '../../edit-recipe/hooks/useEditRecipe/useEditRecipe';
import styles from './RDP.module.css';

export interface Props {
  pairing: PairingWithIngredientDetails;
  ingredients?: ReactNode;
  actionButton?: ReactNode;
  imageOverlay?: ReactNode;
  className?: string;
  dietaryIngredients?: DietaryRestrictionsText[];
  ingredientContainsNeverPeriodicity?: boolean;
}

function renderFeaturedQuote(pairing: Pairing) {
  const { featured_quote: quote } = pairing;
  if (quote && quote.indexOf('|') > -1) {
    return (
      <div className="featuredQuote">
        <div className="quote hr-title-sm">{quote.split('|')[0].trim()}</div>
        <div className="quoteAttribution hr-paragraph">
          {quote.split('|')[1].trim()}
        </div>
      </div>
    );
  } else {
    return null;
  }
}

export function RDP(props: Props) {
  const {
    className,
    actionButton,
    pairing,
    ingredients,
    imageOverlay,
    dietaryIngredients = [],
    ingredientContainsNeverPeriodicity,
  } = props;
  const imgSrc = getPairingDataImg(pairing, 'src');
  const recipeLink = `${window.location.origin}/recipes/${pairing.slug}/`;
  const isDesktop = useMedia(BreakpointQueryLarge);
  const spicyTagsQuery = useTagGroup(tagGroups.spiceLevel);
  const spicyTags =
    spicyTagsQuery.data === undefined
      ? undefined
      : sortBy(spicyTagsQuery.data.tags, ['id']);

  const containsDietaryIngredients = dietaryIngredients.length > 0;

  let warningText;
  let warningTextIntensity;

  if (containsDietaryIngredients) {
    warningText = `This recipe contains ${getWarningText(dietaryIngredients)}`;
    warningTextIntensity = WarningTextStyleVariant.Critical;
  } else if (ingredientContainsNeverPeriodicity) {
    warningText = `This recipe contains items you set to 'never'`;
    warningTextIntensity = WarningTextStyleVariant.Moderate;
  }

  const header = (customerId?: number) => {
    return (
      <Header
        className={containsDietaryIngredients && styles.header}
        orderDate={undefined}
        pairingData={pairing}
        showRating={true}
        hideIcon={true}
        spicyTags={spicyTags}
        isEdited={false}
        customerId={customerId}
      />
    );
  };

  return (
    <Fragment key="rdp">
      <div
        data-testid="rdp"
        className={`rdp-customize pairing-card ${styles.container} ${className}`}
      >
        <div data-testid="rdp-image" className={styles['pairing-image']}>
          {imgSrc ? (
            <div
              className={styles['image-container']}
              style={{ backgroundImage: `url(${imgSrc})` }}
            ></div>
          ) : (
            <div className={styles['image-container']}>
              <IngredientsImageGrid ingredients={pairing.ingredients} />
            </div>
          )}
          {imageOverlay !== undefined && (
            <div className={styles['pairing-image-overlay']}>
              {imageOverlay}
            </div>
          )}
        </div>
        <div
          data-testid="rdp-info-container"
          className={styles['info-container']}
        >
          {warningText && (
            <div className={styles['warning-text-container']}>
              <WarningText
                intensity={warningTextIntensity}
                testid="warning-text"
              >
                {warningText}
              </WarningText>
            </div>
          )}
          <AuthGuard
            authenticated={(auth) => {
              return header(auth.customerData.id);
            }}
            anonymous={() => {
              return header();
            }}
          />

          <div className={styles['body-holder']}>
            {ingredients !== undefined ? (
              ingredients
            ) : (
              <Ingredients ingredients={pairing.ingredients} />
            )}
            {pairing.short_instruction_html && (
              <Instructions
                instructionsHtml={pairing.short_instruction_html}
                open={true}
                hideToggle={true}
              />
            )}
            <NutritionInfo nutritionHTML={pairing.nutrition_html} />
            {renderFeaturedQuote(pairing)}
            <div
              className="share-container hr-mb-8"
              onClick={(evt) => copySharableLink(evt)}
            >
              <div className="share-link-container">
                <div className="link-text">
                  <input
                    className="share-link hr-subtitle-md"
                    name="link"
                    value={recipeLink}
                    readOnly={true}
                  />
                </div>
                <div className="copy-button hr-bold">Copy</div>
              </div>
            </div>
          </div>
          {isDesktop === true && actionButton}
        </div>
      </div>
      {isDesktop === false && actionButton}
    </Fragment>
  );
}
